import React, { useState, useEffect, useCallback } from 'react';
import { useScrollLock } from '../../_utils/useScrollLock';
import ReactDOM from 'react-dom';
import CloseButton from './CloseButton';
import './HelpWalkthrough.css';

// A light, premium "how it works" walkthrough, opened from a `?` button in a
// page header. Mirrors the Vouchers onboarding: a centred pop-up on web, a
// slide-up sheet on mobile, with direction-aware slide transitions.
//
// Props:
//   eyebrow  — small uppercase kicker, e.g. "How services work"
//   slides   — [{ key, title, body, Stage }]  (Stage = an illustration component)
//   onClose  — called when dismissed
export const HelpWalkthrough = ({ eyebrow = 'How it works', slides = [], onClose }) => {
  const TOTAL = slides.length;
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1); // +1 forward, -1 back — drives the enter animation
  const [closing, setClosing] = useState(false);

  const handleClose = useCallback(() => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => { if (onClose) onClose(); }, 240);
  }, [closing, onClose]);

  const goTo = useCallback((n) => {
    const c = Math.max(0, Math.min(TOTAL - 1, n));
    setDir(c >= step ? 1 : -1);
    setStep(c);
  }, [step, TOTAL]);

  const next = useCallback(() => {
    if (step >= TOTAL - 1) { handleClose(); return; }
    goTo(step + 1);
  }, [step, TOTAL, goTo, handleClose]);

  const back = useCallback(() => goTo(step - 1), [step, goTo]);

  // Shared ref-counted scroll lock: overlapping overlays must not restore
  // each other's locked styles.
  useScrollLock(true);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') back();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [handleClose, next, back]);

  if (!TOTAL) return null;
  const slide = slides[step];
  const Stage = slide.Stage;
  const isLast = step === TOTAL - 1;
  const cc = closing ? ' closing' : '';

  const modal = (
    <div className={'hw-overlay' + cc} onClick={handleClose}>
      <div className={'hw-modal' + cc} role="dialog" aria-modal="true" aria-label={eyebrow} onClick={(e) => e.stopPropagation()}>
        <CloseButton className="hw-close" onClick={handleClose} />

        <div className="hw-eyebrow">{eyebrow}</div>

        <div className="hw-carousel">
          <div className="hw-slide" key={step} data-dir={dir > 0 ? 'fwd' : 'back'}>
            {Stage && <div className="hw-stage">{<Stage />}</div>}
            <div className="hw-copy">
              <div className="hw-step-count">Step {step + 1} of {TOTAL}</div>
              <h2 className="hw-title">{slide.title}</h2>
              <p className="hw-body">{slide.body}</p>
            </div>
          </div>
        </div>

        <div className="hw-dots" role="tablist" aria-label="Steps">
          {slides.map((s, i) => (
            <button
              key={s.key}
              type="button"
              className={'hw-dot' + (i === step ? ' is-active' : '')}
              aria-label={`Go to step ${i + 1}`}
              aria-selected={i === step}
              onClick={() => goTo(i)}
            />
          ))}
        </div>

        <div className="hw-footer">
          {step > 0 ? (
            <button type="button" className="hw-btn hw-btn--ghost" onClick={back}>Back</button>
          ) : (
            <button type="button" className="hw-btn hw-btn--ghost" onClick={handleClose}>Skip</button>
          )}
          <button type="button" className="hw-btn hw-btn--primary" onClick={next}>{isLast ? 'Got it' : 'Next'}</button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default HelpWalkthrough;
